<template>
  <UiDrawer
    :is-open="isOpen"
    :width="width"
    color="bg-primary"
    class="ui-sidebar__drawer"
    :style="{ top: isScrollingBottom ? '0' : 'var(--header-height)' }"
    style="height: auto !important"
    @update:is-open="$emit('update:isOpen', $event)"
  >
    <template #prepend-content>
      <slot name="prepend-drawer-content" />
    </template>
    <div class="ui-sidebar__content">
      <div class="ui-sidebar__top">
        <UiSidebarHeader>
          <template #logo>
            <slot name="logo" />
          </template>
        </UiSidebarHeader>
        <UiSidebarMenu
          v-model="menuItemsState"
          :items="menu"
          :is-sidebar-open="isOpen"
        />
        <template v-if="bottomMenuVisible && additionalMenu?.length">
          <UiDivider color="border-secondary" />
          <UiSidebarMenu
            v-model="bottomMenuItemsState"
            :items="additionalMenu"
            :is-sidebar-open="isOpen"
          />
        </template>
      </div>
      <div
        v-if="bottomLinksVisible && additionalBottomLinks?.length"
        class="ui-sidebar__bottom"
      >
        <UiDivider color="border-secondary" />
        <UiSidebarBottomItemList
          :items="additionalBottomLinks"
          :is-sidebar-open="isOpen"
        />
      </div>
    </div>
  </UiDrawer>
  <UiBackdrop :visible="backdropVisible" />
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import {
  UiDrawer,
  UiSidebarMenu,
  UiBackdrop,
  UiDivider,
  UiSidebarBottomItemList,
} from '~/ui';
import UiSidebarHeader from './UiSidebarHeader.vue';
import './UiSidebar.css';
import { useScroll } from '~/infrastructure/composables';
import { type SidebarItem } from '~/app/layouts/sidebar';

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'update:isOpen', value: boolean): void;
  (e: 'backdropVisibleChange', data: boolean): void;
  (e: 'transitionEnd'): void;
}>();

const { isScrollingBottom } = useScroll();

export interface Props {
  menu: SidebarItem[];
  additionalMenu?: SidebarItem[];
  additionalBottomLinks?: SidebarItem[];
  isOpen: boolean;
  width?: string | number;
}

const menuItemsState = ref<string[]>([]);
const bottomMenuItemsState = ref<string[]>([]);

const backdropVisible = computed(() => Boolean(menuItemsState.value.length));

const bottomMenuVisible = computed(() =>
  props.additionalMenu?.some((item) => item.isVisible),
);

const bottomLinksVisible = computed(() =>
  props.additionalBottomLinks?.some((item) => item.isVisible),
);

watch(backdropVisible, (newBackdropVisibleState) => {
  emit('backdropVisibleChange', newBackdropVisibleState);
});
</script>

<style lang="scss" scoped>
.ui-sidebar {
  &__content {
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
  }

  &__top {
    background-color: rgb(var(--v-theme-bg-primary));
    padding: var(--spacing-5) 0;
    display: flex;
    flex-direction: column;
    row-gap: var(--spacing-4);
    max-width: 500px;
    transition: padding-top var(--layout-transition-duration)
      var(--layout-transition-timing);
  }
}

:deep(.app-logo) {
  color: rgb(var(--v-theme-logo-color));
}
</style>
